import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

import { useKitlookPurchaseActionsRef } from '../context/KitlookPurchaseActionsContext'
import type { KitlookSkuMenuPosition } from '../utils/getKitlookSkuMenuPosition'
import { getKitlookSkuMenuPosition } from '../utils/getKitlookSkuMenuPosition'

function isSamePosition(
  current: KitlookSkuMenuPosition | null,
  next: KitlookSkuMenuPosition
) {
  if (!current || current.placement !== next.placement) return false
  if (
    current.left !== next.left ||
    current.width !== next.width ||
    current.maxHeight !== next.maxHeight
  ) {
    return false
  }

  if (current.placement === 'down' && next.placement === 'down') {
    return current.top === next.top
  }

  if (current.placement === 'up' && next.placement === 'up') {
    return current.bottom === next.bottom
  }

  return false
}

interface UseKitlookSkuMenuPositionParams {
  triggerRef: RefObject<HTMLElement | null>
  menuRef: RefObject<HTMLElement | null>
  listRef: RefObject<HTMLElement | null>
  optionsCount: number
}

export function useKitlookSkuMenuPosition({
  triggerRef,
  menuRef,
  listRef,
  optionsCount,
}: UseKitlookSkuMenuPositionParams) {
  const purchaseActionsRef = useKitlookPurchaseActionsRef()
  const [position, setPosition] = useState<KitlookSkuMenuPosition | null>(null)

  useLayoutEffect(() => {
    const updatePosition = () => {
      const trigger = triggerRef.current

      if (!trigger) return

      const triggerRect = trigger.getBoundingClientRect()
      const menu = menuRef.current

      if (menu) {
        menu.style.width = `${triggerRect.width + 2}px`
      }

      const actions = purchaseActionsRef?.current
      const panel = actions?.parentElement
      const boundaryTop = panel?.getBoundingClientRect().top ?? 0
      const boundaryBottom =
        actions?.getBoundingClientRect().top ?? window.innerHeight

      if (
        triggerRect.bottom <= boundaryTop ||
        triggerRect.top >= boundaryBottom
      ) {
        setPosition((current) => (current === null ? current : null))

        return
      }

      const listHeight = listRef.current?.scrollHeight ?? 0
      const menuBorder = menu ? menu.offsetHeight - menu.clientHeight : 0

      const nextPosition = getKitlookSkuMenuPosition({
        trigger: {
          top: triggerRect.top,
          bottom: triggerRect.bottom,
          left: triggerRect.left,
          width: triggerRect.width,
        },
        boundaryTop,
        boundaryBottom,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
        contentHeight: listHeight > 0 ? listHeight + menuBorder : 0,
      })

      setPosition((current) =>
        isSamePosition(current, nextPosition) ? current : nextPosition
      )
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    document.addEventListener('scroll', updatePosition, true)

    const resizeObserver = new ResizeObserver(updatePosition)
    const actions = purchaseActionsRef?.current
    const observedElements = [
      triggerRef.current,
      actions,
      actions?.parentElement,
    ]

    observedElements.forEach((element) => {
      if (element) resizeObserver.observe(element)
    })

    return () => {
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('scroll', updatePosition, true)
      resizeObserver.disconnect()
    }
  }, [listRef, menuRef, optionsCount, purchaseActionsRef, triggerRef])

  return position
}
